// Svarsskalan är femgradig. De tre ursprungliga värdena ("låg", "medel",
// "hög") behålls oförändrade så att äldre bedömningar fungerar utan migrering
// – de motsvarar steg 2, 3 och 4 på den nya skalan.
export type SKRBedomning = "mycket_låg" | "låg" | "medel" | "hög" | "mycket_hög";

export interface SKRScaleStep {
  value: SKRBedomning;
  label: string;
  /** 1–5, används i nivåberäkningen. */
  score: number;
  badgeClass: string;
  labelClass: string;
}

export const SKR_SCALE: SKRScaleStep[] = [
  {
    value: "mycket_låg",
    label: "Mycket låg",
    score: 1,
    badgeClass: "bg-emerald-100 text-emerald-900 border-emerald-200",
    labelClass: "text-emerald-800",
  },
  {
    value: "låg",
    label: "Låg",
    score: 2,
    badgeClass: "bg-green-100 text-green-800 border-green-200",
    labelClass: "text-green-700",
  },
  {
    value: "medel",
    label: "Medel",
    score: 3,
    badgeClass: "bg-yellow-100 text-yellow-800 border-yellow-200",
    labelClass: "text-yellow-700",
  },
  {
    value: "hög",
    label: "Hög",
    score: 4,
    badgeClass: "bg-orange-100 text-orange-900 border-orange-200",
    labelClass: "text-orange-700",
  },
  {
    value: "mycket_hög",
    label: "Mycket hög",
    score: 5,
    badgeClass: "bg-red-100 text-red-800 border-red-200",
    labelClass: "text-red-700",
  },
];

const stepFor = (value?: SKRBedomning | null): SKRScaleStep | undefined =>
  value ? SKR_SCALE.find((s) => s.value === value) : undefined;

/** Poäng 1–5, eller null när frågan inte är besvarad. */
export const skrScore = (value?: SKRBedomning | null): number | null =>
  stepFor(value)?.score ?? null;

export const skrLabel = (value?: SKRBedomning | null): string =>
  stepFor(value)?.label ?? "Ej bedömd";

/** Etikett med siffra, för export och sammanställningar. */
export const skrLabelWithScore = (value?: SKRBedomning | null): string => {
  const step = stepFor(value);
  return step ? `${step.label} (${step.score})` : "Ej bedömd";
};

export const skrBadgeClass = (value?: SKRBedomning | null): string =>
  stepFor(value)?.badgeClass ?? "bg-gray-100 text-gray-500 border-gray-200";

export interface SKRFraga {
  id: string;
  text: string;
  kategori: "Storlek" | "Komplexitet" | "Risknivå" | "Förväntad åtgärdsnivå";
  hjalptext?: string;
  /** Tröskelvärden för de fem stegen, visas i hjälptexten. */
  skala?: string;
}


export interface SKRSvarData {
  svar?: SKRBedomning;
  kommentar?: string;
  atgard?: string;
  isHandled?: boolean;
  _lastUpdated?: string;
}

export interface SKRData {
  assessmentId: string;
  projektId?: string;
  folderId?: string;
  projektNamn?: string;
  projektbeskrivning?: string;
  budget?: string;
  byggtid?: string;
  bakgrund?: string;
  syfte?: string;
  omfattning?: string;
  ansvar?: string;
  upprattatAv?: string;
  datum?: string;
  projektledare?: string;
  handlaggandeBASP?: string;
  ort?: string;
  namnfortydligande?: string;
  svar: Record<string, SKRSvarData>;
  status?: 'draft' | 'active' | 'completed' | 'archived';
  completedAt?: string;
  completedBy?: string;
  createdAt?: string;
  updatedAt?: string;
  schemaVersion?: string;
}

export interface SKRExempelProjekt {
  id: string;
  namn: string;
  beskrivning: string;
  budget: string;
  byggtid: string;
  typ: string;
  icon: "🟩" | "🟨" | "🟥";
  kategori: "litet" | "medel" | "stort";
  svar: Record<string, SKRSvarData>;
}